import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { Box } from "@mui/material";
import MedicalServicesOutlinedIcon from "@mui/icons-material/MedicalServicesOutlined";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import HomeIcon from "@mui/icons-material/Home";
import AssignmentIndOutlinedIcon from "@mui/icons-material/AssignmentIndOutlined";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { ColorModeContext } from "../../../../theme/ColorModeContext";

const getActiveTab = (pathname: string) => {
  if (
    pathname.startsWith("/opd-clinical") ||
    pathname.startsWith("/report-certificate")
  ) {
    return "opd";
  }
  if (pathname.startsWith("/calendar")) return "calendar";
  if (pathname.startsWith("/dashboard")) return "home";
  if (pathname.startsWith("/patient")) return "patient";
  if (pathname.startsWith("/profile")) return "profile";
  return "";
};

export const MobileBottomNav: React.FC = React.memo(() => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { mode } = React.useContext(ColorModeContext);
  const isDark = mode === "dark";
  const active = getActiveTab(pathname);

  const go = (tab: string) => {
    const today = format(new Date(), "yyyy-MM-dd");
    const dateRange = `fd=${today}&ed=${today}`;
    if (tab === "home") {
      navigate("/dashboard");
    } else if (tab === "calendar") {
      navigate("/calendar");
    } else if (tab === "opd") {
      navigate(`/opd-clinical?tab=waiting&page=1&${dateRange}&q=`);
    } else if (tab === "patient") {
      navigate("/patient-master?page=1&q=");
    } else if (tab === "profile") {
      navigate("/profile");
    }
  };

  const items = [
    {
      id: "home",
      label: "Home",
      icon: active === "home" ? <HomeIcon /> : <HomeOutlinedIcon />,
    },
    {
      id: "calendar",
      label: "Calendar",
      icon:
        active === "calendar" ? (
          <CalendarMonthIcon />
        ) : (
          <CalendarMonthOutlinedIcon />
        ),
    },
    {
      id: "opd",
      label: "OPD",
      icon:
        active === "opd" ? (
          <MedicalServicesIcon />
        ) : (
          <MedicalServicesOutlinedIcon />
        ),
    },
    {
      id: "patient",
      label: "Patient",
      icon:
        active === "patient" ? (
          <AssignmentIndIcon />
        ) : (
          <AssignmentIndOutlinedIcon />
        ),
    },
    {
      id: "profile",
      label: "Profile",
      icon:
        active === "profile" ? (
          <AccountCircleIcon />
        ) : (
          <AccountCircleOutlinedIcon />
        ),
    },
  ];

  const selectedRing = isDark
    ? "rgba(255, 255, 255, 0.22)"
    : "rgba(40, 92, 219, 0.12)";
  const iconIdle = isDark ? "#ffffff" : "#414D56";

  return (
    <Box
      sx={{
        position: "fixed",
        left: 14,
        right: 14,
        bottom: "calc(10px + env(safe-area-inset-bottom))",
        zIndex: 1200,
        display: "flex",
        alignItems: "center",
        height: 50,
        px: 0.75,
        borderRadius: "30px",
        backgroundColor: isDark
          ? "rgba(0, 0, 0, 0.55)"
          : "rgba(255, 255, 255, 0.75)",
        backgroundImage: "none",
        backdropFilter: "blur(2px)",
        WebkitBackdropFilter: "blur(2px)",
        border: isDark
          ? "1px solid rgba(255, 255, 255, 0.14)"
          : "1px solid rgba(255, 255, 255, 0.85)",
        boxShadow: isDark
          ? "0 8px 24px rgba(0, 0, 0, 0.28)"
          : "0 8px 24px rgba(0, 0, 0, 0.1)",
      }}
    >
      {items.map((item) => {
        const selected = active === item.id;
        return (
          <Box
            key={item.id}
            component="button"
            type="button"
            aria-label={item.label}
            onClick={() => go(item.id)}
            sx={{
              flex: 1,
              height: 35,
              border: 0,
              p: 0,
              mx: 0.25,
              borderRadius: "24px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              color: selected ? "#285cdb" : iconIdle,
              backgroundColor: selected ? selectedRing : "transparent",
              outline: "none",
              boxShadow: "none",
              WebkitTapHighlightColor: "transparent",
              appearance: "none",
              "&:hover, &:focus, &:focus-visible, &:active": {
                outline: "none",
                boxShadow: "none",
                backgroundColor: selected ? selectedRing : "transparent",
              },
              "&::-moz-focus-inner": {
                border: 0,
              },
              "& .MuiSvgIcon-root": {
                fontSize: 26,
              },
            }}
          >
            {item.icon}
          </Box>
        );
      })}
    </Box>
  );
});

MobileBottomNav.displayName = "MobileBottomNav";
